import { isRelativeDateToken } from './relative-date';
import {
    DEFAULT_OPERATORS,
    type FieldConfig,
    type FieldType,
    type FilterOperator,
    type FilterValue,
    OPERATOR_LABELS,
} from './types';

/**
 * Get available operators for a specific field type
 *
 * @param fieldType - The type of field (text, number, date, etc.)
 * @param customOperators - Optional custom operators to override defaults
 * @returns Array of available operators for the field type
 *
 * @example
 * ```typescript
 * // Get default operators for text field
 * const textOperators = getOperatorsForFieldType('text');
 * // Returns: ['equals', 'not', 'contains', 'startsWith', 'endsWith', 'isNull', 'isNotNull']
 *
 * // Override with custom operators
 * const customOps = getOperatorsForFieldType('text', ['equals', 'contains']);
 * // Returns: ['equals', 'contains']
 * ```
 */
export function getOperatorsForFieldType(fieldType: FieldType, customOperators?: FilterOperator[]): FilterOperator[] {
    return customOperators || DEFAULT_OPERATORS[fieldType] || ['equals'];
}

/**
 * Get human-readable label for a filter operator
 *
 * @param operator - The filter operator to get label for
 * @returns Human-readable label or the operator itself if no label exists
 *
 * @example
 * ```typescript
 * getOperatorLabel('equals'); // Returns: 'is'
 * getOperatorLabel('in'); // Returns: 'is any of'
 * getOperatorLabel('gt', 'date'); // Returns: 'is after'
 * getOperatorLabel('customOp'); // Returns: 'customOp' (fallback)
 * ```
 */
export function getOperatorLabel(operator: FilterOperator, fieldType?: FieldType): string {
    // Date fields read more naturally with temporal phrasing than "greater/less than".
    if (fieldType === 'date' || fieldType === 'datetime') {
        const dateLabels: Partial<Record<FilterOperator, string>> = {
            gt: 'is after',
            gte: 'is on or after',
            lt: 'is before',
            lte: 'is on or before',
        };
        const dateLabel = dateLabels[operator];
        if (dateLabel) return dateLabel;
    }
    return OPERATOR_LABELS[operator] || operator;
}

/**
 * Check if a filter operator requires a value input
 *
 * @param operator - The filter operator to check
 * @returns true if operator requires a value, false for null-check operators
 *
 * @example
 * ```typescript
 * operatorRequiresValue('equals'); // true - needs a comparison value
 * operatorRequiresValue('isNull'); // false - no value needed
 * operatorRequiresValue('contains'); // true - needs text to search for
 * ```
 */
export function operatorRequiresValue(operator: FilterOperator): boolean {
    return operator !== 'isNull' && operator !== 'isNotNull';
}

/** A scalar that contributes nothing to a filter: null, undefined, or a blank/whitespace string. */
function isEmptyScalar(value: unknown): boolean {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

/**
 * Canonical "does this filter contribute a condition?" emptiness check — the single source of
 * truth shared by `buildWhereClause`, the saved-view capture/reconcile (`isMeaningfulFilter`), and
 * the live filter bar's onChange gating. Value-less operators (is empty / is not empty) are never
 * empty; otherwise a null/undefined/blank value (or an incomplete `between` pair / all-blank list)
 * is empty. Blank/whitespace-only strings count as empty everywhere.
 */
export function isFilterValueEmpty(operator: FilterOperator, value: unknown): boolean {
    if (!operatorRequiresValue(operator)) return false;
    if (Array.isArray(value)) {
        if (operator === 'between' || operator === 'notBetween') {
            return value.length !== 2 || value.some(isEmptyScalar);
        }
        return value.length === 0 || value.every(isEmptyScalar);
    }
    return isEmptyScalar(value);
}

/**
 * Resolve the default operator + value for a field — the single source of truth shared by the
 * filter hook (initial/added filters) and the filter-bar/builder placeholders. Precedence for the
 * operator: the field's explicit `defaultOperator`, then `contains` for text fields when
 * `preferContains` is set (the friendlier substring default), then the first available operator.
 */
export function resolveFieldDefaults(
    fieldConfig: Pick<FieldConfig<any, any>, 'type' | 'operators' | 'defaultOperator' | 'defaultValue'>,
    opts?: { preferContains?: boolean },
): { operator: FilterOperator; value: FilterValue } {
    const type = fieldConfig.type || 'text';
    const availableOperators = getOperatorsForFieldType(type, fieldConfig.operators);
    const preferred =
        opts?.preferContains && (type === 'text' || type === 'textSearch') && availableOperators.includes('contains')
            ? 'contains'
            : undefined;
    const operator = fieldConfig.defaultOperator || preferred || availableOperators[0] || 'equals';
    const value = fieldConfig.defaultValue ?? getDefaultValueForOperator(operator, type);
    return { operator, value };
}

/**
 * Check if a filter operator requires multiple values (range operators)
 *
 * @param operator - The filter operator to check
 * @returns true if operator requires two values (min and max)
 *
 * @example
 * ```typescript
 * operatorRequiresMultipleValues('between'); // true - needs [min, max]
 * operatorRequiresMultipleValues('equals'); // false - single value
 * operatorRequiresMultipleValues('notBetween'); // true - needs [min, max]
 * ```
 */
export function operatorRequiresMultipleValues(operator: FilterOperator): boolean {
    return operator === 'between' || operator === 'notBetween';
}

/**
 * Check if a filter operator supports array values
 *
 * @param operator - The filter operator to check
 * @returns true if operator can accept an array of values
 *
 * @example
 * ```typescript
 * operatorSupportsArrayValue('in'); // true - WHERE field IN [1, 2, 3]
 * operatorSupportsArrayValue('notIn'); // true - WHERE field NOT IN [1, 2, 3]
 * operatorSupportsArrayValue('equals'); // false - single value only
 * ```
 */
export function operatorSupportsArrayValue(operator: FilterOperator): boolean {
    return operator === 'in' || operator === 'notIn';
}

/**
 * Normalise a filter value to the shape accepted by its current operator.
 *
 * Filters can arrive without passing through the interactive operator-change path (for example,
 * from a saved view or deep link). Scalar operators must therefore collapse a stale array value
 * at the where-building boundary as well. JSON values are excluded because arrays are valid JSON
 * scalars rather than multi-select state.
 */
export function normalizeValueForOperator(
    currentValue: FilterValue,
    operator: FilterOperator,
    fieldType: FieldType,
): FilterValue {
    if (
        !operatorRequiresValue(operator) ||
        fieldType === 'json' ||
        operatorSupportsArrayValue(operator) ||
        operatorRequiresMultipleValues(operator) ||
        !Array.isArray(currentValue)
    ) {
        return currentValue;
    }

    return currentValue[0] ?? null;
}

/**
 * Infer field type from a value
 *
 * Analyzes a value and determines the most appropriate field type.
 * Useful for auto-detecting field types from data.
 *
 * @param value - The value to analyze
 * @returns The inferred field type
 *
 * @example
 * ```typescript
 * inferFieldType('hello'); // 'text'
 * inferFieldType(123); // 'number'
 * inferFieldType(true); // 'boolean'
 * inferFieldType(['a', 'b']); // 'multiselect'
 * inferFieldType('2024-01-01'); // 'date'
 * inferFieldType('2024-01-01T10:30:00'); // 'datetime'
 * inferFieldType(BigInt(123)); // 'bigint'
 * inferFieldType({ key: 'value' }); // 'json'
 * inferFieldType(null); // 'text' (default fallback)
 * ```
 */
export function inferFieldType(value: any): FieldType {
    if (value === null || value === undefined) {
        return 'text'; // Default fallback
    }

    if (typeof value === 'string') {
        // Check if it's a date string
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        const datetimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

        if (dateRegex.test(value)) return 'date';
        if (datetimeRegex.test(value)) return 'datetime';

        return 'text';
    }

    if (typeof value === 'number') {
        return 'number';
    }

    if (typeof value === 'bigint') {
        return 'bigint';
    }

    if (typeof value === 'boolean') {
        return 'boolean';
    }

    if (value instanceof Date) {
        return 'datetime';
    }

    if (Array.isArray(value)) {
        return 'multiselect';
    }

    if (typeof value === 'object') {
        return 'json';
    }

    return 'text';
}

/**
 * Convert filter operator and value to Prisma where condition
 *
 * Generates a Prisma-compatible where clause based on the operator and value.
 * This is a generic implementation - use custom where builders for complex fields.
 *
 * @param field - The database field name
 * @param operator - The filter operator to apply
 * @param value - The value(s) to filter by
 * @returns Prisma where condition object
 *
 * @example
 * ```typescript
 * // Simple equality
 * buildWhereCondition('name', 'equals', 'John');
 * // Returns: { name: 'John' }
 *
 * // Case-insensitive contains
 * buildWhereCondition('email', 'contains', 'gmail');
 * // Returns: { email: { contains: 'gmail', mode: 'insensitive' } }
 *
 * // Range query
 * buildWhereCondition('age', 'between', [18, 65]);
 * // Returns: { AND: [{ age: { gte: 18 } }, { age: { lte: 65 } }] }
 *
 * // Null check
 * buildWhereCondition('deletedAt', 'isNull', null);
 * // Returns: { deletedAt: null }
 *
 * // Array inclusion
 * buildWhereCondition('status', 'in', ['active', 'pending']);
 * // Returns: { status: { in: ['active', 'pending'] } }
 * ```
 */
export function buildWhereCondition(field: string, operator: FilterOperator, value: any): Record<string, any> {
    // Handle null operators
    if (operator === 'isNull') {
        return { [field]: null };
    }

    if (operator === 'isNotNull') {
        return { [field]: { not: null } };
    }

    // Handle value-based operators
    switch (operator) {
        case 'equals':
            return { [field]: value };

        case 'not':
            return { [field]: { not: value } };

        case 'in':
            return { [field]: { in: Array.isArray(value) ? value : [value] } };

        case 'notIn':
            return { [field]: { notIn: Array.isArray(value) ? value : [value] } };

        case 'contains':
            return { [field]: { contains: value, mode: 'insensitive' } };

        case 'startsWith':
            return { [field]: { startsWith: value, mode: 'insensitive' } };

        case 'endsWith':
            return { [field]: { endsWith: value, mode: 'insensitive' } };

        case 'gt':
            return { [field]: { gt: value } };

        case 'gte':
            return { [field]: { gte: value } };

        case 'lt':
            return { [field]: { lt: value } };

        case 'lte':
            return { [field]: { lte: value } };

        case 'between':
            if (Array.isArray(value) && value.length === 2) {
                return {
                    AND: [{ [field]: { gte: value[0] } }, { [field]: { lte: value[1] } }],
                };
            }
            return {};

        case 'notBetween':
            if (Array.isArray(value) && value.length === 2) {
                return {
                    OR: [{ [field]: { lt: value[0] } }, { [field]: { gt: value[1] } }],
                };
            }
            return {};

        default:
            return { [field]: value };
    }
}

/**
 * Get appropriate default value for a filter operator and field type
 *
 * Returns a sensible default value based on the operator requirements and field type.
 * Used when adding new filters to provide initial values.
 *
 * @param operator - The filter operator
 * @param fieldType - The type of field being filtered
 * @returns Default value appropriate for the operator and field type
 *
 * @example
 * ```typescript
 * // Null operators don't need values
 * getDefaultValueForOperator('isNull', 'text'); // null
 *
 * // Range operators need two values
 * getDefaultValueForOperator('between', 'number'); // [null, null]
 *
 * // Array operators start empty
 * getDefaultValueForOperator('in', 'select'); // []
 *
 * // Field type specific defaults
 * getDefaultValueForOperator('equals', 'text'); // ''
 * getDefaultValueForOperator('equals', 'boolean'); // false
 * getDefaultValueForOperator('equals', 'multiselect'); // []
 * getDefaultValueForOperator('equals', 'number'); // null (not 0 to avoid accidental filtering)
 * ```
 */
export function getDefaultValueForOperator(operator: FilterOperator, fieldType: FieldType): FilterValue {
    if (!operatorRequiresValue(operator)) {
        return null;
    }

    if (operatorRequiresMultipleValues(operator)) {
        return [null, null];
    }

    if (operatorSupportsArrayValue(operator)) {
        return [];
    }

    // Return appropriate default based on field type
    switch (fieldType) {
        case 'text':
            return '';
        case 'number':
        case 'bigint':
            return null; // Changed from 0 to null to prevent immediate filtering
        case 'boolean':
            return false;
        case 'date':
        case 'datetime':
            return null;
        case 'select':
            return null;
        case 'multiselect':
            return [];
        case 'json':
            return null;
        default:
            return null;
    }
}

/**
 * Transform a filter value when the operator changes.
 * Attempts to preserve the value when operators are compatible.
 *
 * @param currentValue - The current filter value
 * @param fromOperator - The previous operator
 * @param toOperator - The new operator
 * @param fieldType - The type of field being filtered
 * @returns Transformed value appropriate for the new operator
 *
 * @example
 * ```typescript
 * // Same value structure - preserve value
 * transformValueForOperator('2024-01-15', 'equals', 'gt', 'date');
 * // Returns: '2024-01-15'
 *
 * // Single to multi-value - wrap in array
 * transformValueForOperator('2024-01-15', 'equals', 'between', 'date');
 * // Returns: ['2024-01-15', null]
 *
 * // Multi to single-value - extract first non-null
 * transformValueForOperator(['2024-01-15', '2024-02-15'], 'between', 'equals', 'date');
 * // Returns: '2024-01-15'
 * ```
 */
export function transformValueForOperator(
    currentValue: unknown,
    fromOperator: FilterOperator,
    toOperator: FilterOperator,
    fieldType: FieldType,
): unknown {
    const fromRequiresMultiple = operatorRequiresMultipleValues(fromOperator);
    const toRequiresMultiple = operatorRequiresMultipleValues(toOperator);
    const fromRequiresValue = operatorRequiresValue(fromOperator);
    const toRequiresValue = operatorRequiresValue(toOperator);

    // If new operator doesn't need a value, return null
    if (!toRequiresValue) {
        return null;
    }

    // A relative date token only resolves under "is within": start it fresh, and clear the token
    // when leaving it, so a switch never leaves a stale value (e.g. chip "is after Last 30 days").
    if (toOperator === 'withinRelative') return null;
    if (isRelativeDateToken(currentValue)) return getDefaultValueForOperator(toOperator, fieldType);

    // If old operator didn't have a meaningful value, return default
    if (!fromRequiresValue || currentValue === null || currentValue === undefined) {
        return getDefaultValueForOperator(toOperator, fieldType);
    }

    // Array-value operators (in/notIn) are not range operators, so they otherwise look
    // structurally identical to scalar operators here. Collapse their selection when moving
    // back to a scalar operator so the where builder does not receive an invalid array value.
    if (operatorSupportsArrayValue(fromOperator) && !operatorSupportsArrayValue(toOperator) && !toRequiresMultiple) {
        if (Array.isArray(currentValue) && currentValue.length > 0) {
            return currentValue[0] ?? getDefaultValueForOperator(toOperator, fieldType);
        }
        return Array.isArray(currentValue) ? getDefaultValueForOperator(toOperator, fieldType) : currentValue;
    }

    // Same value structure - preserve as-is
    if (fromRequiresMultiple === toRequiresMultiple) {
        return currentValue;
    }

    // Multi-value → Single-value: extract first non-null value
    if (fromRequiresMultiple && !toRequiresMultiple) {
        if (Array.isArray(currentValue) && currentValue.length > 0) {
            return currentValue[0] ?? currentValue[1] ?? getDefaultValueForOperator(toOperator, fieldType);
        }
        return getDefaultValueForOperator(toOperator, fieldType);
    }

    // Single-value → Multi-value: wrap in array
    if (!fromRequiresMultiple && toRequiresMultiple) {
        return [currentValue, null];
    }

    // Fallback to default
    return getDefaultValueForOperator(toOperator, fieldType);
}
